import { Target, Eye, Award, Shield, FileBadge, MapPin, Compass } from "lucide-react";
import Reveal from "@/components/Reveal";
import { useI18n } from "@/lib/i18n";

const REGISTRATIONS = [
  { label: "CIN", value: "U66190GJ2025PTC171248" },
  // LR-004 · Money-Lender chip TEMPORARILY HIDDEN pending renewal
  // certificate covering the period past 31/03/2026. Historical record
  // Tapi/56/2021 is preserved in /app/backend/legal_registry.py; do NOT
  // restore this chip on any surface until the renewal is verified.
  { label: "UDYAM", value: "UDYAM-GJ-24-0178730" },
  { label: "GSTIN", value: "24AVFFP6564L2Z4" },
  { label: "PAN", value: "AALCN1559R" },
  { label: "TAN", value: "AHMN15110E" },
];

const VALUES = [
  { icon: Shield, k: "Integrity" },
  { icon: FileBadge, k: "Compliance" },
  { icon: Target, k: "Growth" },
  { icon: Award, k: "Trust" },
];

export default function About() {
  const { t } = useI18n();
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-[#0A1F44] text-white py-24 md:py-32 overflow-hidden noise-overlay"
    >
      <div className="pointer-events-none absolute -top-32 right-1/3 w-[520px] h-[520px] rounded-full border border-[#D4AF37]/10" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Left: Intro */}
          <div className="lg:col-span-5">
            <Reveal>
              <span className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">
                {t("about.eyebrow")}
              </span>
              <h2
                data-testid="about-heading"
                className="font-display text-[40px] md:text-[58px] mt-4 leading-[1.05]"
              >
                Sahyog Finance &amp; <br />
                <em className="not-italic text-[#D4AF37]">Noble Infinity</em>.
              </h2>
              <p className="mt-6 text-white/70 leading-relaxed">
                {t("about.intro")}
              </p>
              <p className="mt-4 text-white/70 leading-relaxed">
                {t("about.intro2")}
              </p>

              {/* Registration chips */}
              <div className="mt-7 flex flex-wrap gap-2">
                {REGISTRATIONS.map((r) => (
                  <span
                    key={r.label}
                    data-testid={`about-reg-${r.label.toLowerCase()}`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-[#D4AF37]/30 text-[11px]"
                  >
                    <span className="text-[#D4AF37] font-semibold">{r.label}</span>
                    <span className="text-white/80">{r.value}</span>
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Right: Mission / Vision / Core Values */}
          <div className="lg:col-span-7 space-y-5">
            <Reveal>
              <div className="grid sm:grid-cols-2 gap-5">
                <div
                  data-testid="about-pillar-mission"
                  className="rounded-2xl p-7 border border-[#D4AF37]/25 bg-white/[0.03] hover:bg-white/[0.06] hover:border-[#D4AF37]/60 transition-all"
                >
                  <div className="tile-icon">
                    <Target size={24} strokeWidth={1.75} />
                  </div>
                  <h3 className="mt-5 font-display text-xl">{t("about.mission")}</h3>
                  <p className="mt-2 text-white/65 text-sm leading-relaxed">
                    <span className="text-[#F0D788] uppercase tracking-wider text-[11px] block mb-1.5">
                      Your Trust · Our Service · Your Success
                    </span>
                    Building Wealth, Securing Futures &amp; Creating Value — through inclusive
                    finance that respects every borrower, regardless of credit history.
                  </p>
                </div>
                <div
                  data-testid="about-pillar-vision"
                  className="rounded-2xl p-7 border border-[#D4AF37]/25 bg-white/[0.03] hover:bg-white/[0.06] hover:border-[#D4AF37]/60 transition-all"
                >
                  <div className="tile-icon">
                    <Eye size={24} strokeWidth={1.75} />
                  </div>
                  <h3 className="mt-5 font-display text-xl">{t("about.vision")}</h3>
                  <p className="mt-2 text-white/65 text-sm leading-relaxed">
                    To become Bharat&apos;s most trusted inclusive-lending platform — empowering
                    farmers, micro-businesses and first-time borrowers across rural &amp;
                    semi-urban India.
                  </p>
                </div>
              </div>
            </Reveal>

            {/* Core Values strip */}
            <Reveal delay={120}>
              <div
                data-testid="about-values"
                className="rounded-2xl p-7 border border-[#D4AF37]/25 bg-gradient-to-r from-[#D4AF37]/10 to-transparent"
              >
                <div className="flex items-center gap-3">
                  <Compass size={18} className="text-[#D4AF37]" />
                  <span className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">
                    {t("about.values")}
                  </span>
                </div>
                <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {VALUES.map(({ icon: Icon, k }) => (
                    <div
                      key={k}
                      data-testid={`about-value-${k.toLowerCase()}`}
                      className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.04] border border-[#D4AF37]/15"
                    >
                      <span className="w-9 h-9 rounded-md grid place-items-center bg-[#D4AF37]/15 text-[#F0D788]">
                        <Icon size={16} />
                      </span>
                      <span className="font-display text-lg">{k}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Stat band */}
        <Reveal delay={150}>
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 rounded-3xl border border-[#D4AF37]/25 bg-gradient-to-b from-white/[0.04] to-transparent p-6 md:p-10">
            {[
              { k: "9", v: "Loan products" },
              { k: "6.5% – 15%", v: "ROI range" },
              { k: "0", v: "CIBIL needed" },
              { k: "24h", v: "Approval time" },
            ].map((s) => (
              <div
                key={s.v}
                data-testid={`about-stat-${s.v.split(" ")[0].toLowerCase()}`}
              >
                <div className="font-display text-3xl md:text-4xl text-[#F0D788]">{s.k}</div>
                <div className="mt-1 text-white/60 text-[12px] uppercase tracking-wider">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Location footer */}
        <Reveal delay={250}>
          <div
            data-testid="about-location"
            className="mt-10 flex items-center justify-center gap-3 text-white/55 text-sm"
          >
            <MapPin size={14} className="text-[#D4AF37]" />
            Registered Office: A-106, Ambaji Complex, Ambaji Mata Road, Vyara, Tapi, Gujarat — 394650
          </div>
        </Reveal>
      </div>
    </section>
  );
}
